import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useActiveCourse } from './ActiveCourseContext';
import './ActiveCourseBar.css';

const getCourseLabel = (course) => {
  if (!course) return '';
  const code = (course.course_code || '').trim().toUpperCase();
  const name = course.course_name || `Ders ${course.course_id}`;
  return code ? `${code} · ${name}` : name;
};

function ActiveCourseBar() {
  const context = useActiveCourse();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef(null);

  useEffect(() => {
    const closeOnOutsideClick = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) setIsOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, []);

  const filteredCourses = useMemo(() => {
    if (!context) return [];
    const normalizedQuery = query.trim().toLocaleLowerCase('tr');
    if (!normalizedQuery) return context.courses;
    return context.courses.filter((course) => [
      course.course_code,
      course.course_name,
      course.program_name,
      course.faculty_name,
    ].some((value) => String(value || '').toLocaleLowerCase('tr').includes(normalizedQuery)));
  }, [context, query]);

  if (!context) return null;

  const { activeCourse, activeCourseId, clearActiveCourse, coursesLoading, setActiveCourseId } = context;
  const selectCourse = (courseId) => {
    setActiveCourseId(courseId);
    setQuery('');
    setIsOpen(false);
  };

  return (
    <section className="active-course-bar" aria-label="Aktif ders seçimi" ref={rootRef}>
      <div className="active-course-bar__icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
          <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
        </svg>
      </div>
      <div className="active-course-bar__summary">
        <span className="active-course-bar__eyebrow">AKTİF DERS</span>
        {coursesLoading ? (
          <span className="active-course-bar__name is-loading">Dersler yükleniyor…</span>
        ) : activeCourse ? (
          <>
            <strong className="active-course-bar__name">{getCourseLabel(activeCourse)}</strong>
            {(activeCourse.program_name || activeCourse.faculty_name) && (
              <span className="active-course-bar__detail">
                {[activeCourse.program_name, activeCourse.faculty_name].filter(Boolean).join(' · ')}
              </span>
            )}
          </>
        ) : (
          <span className="active-course-bar__name is-empty">Ders seçilmedi</span>
        )}
      </div>
      <div className="active-course-bar__actions">
        <button
          type="button"
          className="active-course-bar__change"
          onClick={() => { setIsOpen((open) => !open); setQuery(''); }}
          disabled={coursesLoading}
          aria-expanded={isOpen}
        >
          {activeCourse ? 'Ders Değiştir' : 'Ders Seç'}
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
        {activeCourseId && (
          <button type="button" className="active-course-bar__clear" onClick={clearActiveCourse} aria-label="Aktif dersi temizle" title="Aktif dersi temizle">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        )}
      </div>

      {isOpen && (
        <div className="active-course-bar__menu">
          <div className="active-course-bar__search">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <circle cx="11" cy="11" r="7" /><line x1="16" y1="16" x2="21" y2="21" />
            </svg>
            <input autoFocus value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Ders kodu veya adı ara…" />
          </div>
          <div className="active-course-bar__list" role="listbox" aria-label="Dersler">
            {filteredCourses.length === 0 ? (
              <p className="active-course-bar__no-results">Ders bulunamadı.</p>
            ) : filteredCourses.map((course) => {
              const selected = String(course.course_id) === activeCourseId;
              return (
                <button type="button" role="option" aria-selected={selected} key={course.course_id} className={`active-course-bar__option ${selected ? 'is-selected' : ''}`} onClick={() => selectCourse(course.course_id)}>
                  <span>{getCourseLabel(course)}</span>
                  {(course.program_name || course.faculty_name) && <small>{[course.program_name, course.faculty_name].filter(Boolean).join(' · ')}</small>}
                  {selected && <b aria-hidden="true">✓</b>}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
}

export default ActiveCourseBar;
